import type { IconNameType } from "@UI/types/icons.ts";
import type { NotificationState } from "@UI/types/notifications.ts";
import type { FC, MouseEventHandler, ReactNode } from "react";
import DClearButton from "@UI/components/Atoms/Button/DClearButton.tsx";
import DIcon from "@UI/components/Atoms/Icon/DIcon.tsx";
import classNames from "classnames";

interface Props {
  label?: ReactNode;
  description?: ReactNode;
  size?: "small" | "medium" | "large";
  state?: NotificationState;
  icon?: IconNameType;
  style?: "inline" | "floating";
  action?: ReactNode;
  role?: "alert" | "status";
  onDismiss?: MouseEventHandler;
  plain?: boolean;
}

const STATE_ICONS: Record<NotificationState, IconNameType> = {
  error: "error",
  warning: "warning",
  success: "check-circle",
  info: "info",
  neutral: "question-mark-help",
};

const DNotification: FC<Props> = ({
  label,
  description,
  size = "small",
  state = "neutral",
  icon,
  style = "inline",
  action,
  role: roleOverride,
  onDismiss,
  plain = false,
}) => {
  const floating = style === "floating";
  const lineAligned = size === "small" && !floating && !action && !onDismiss;
  const classes = classNames(
    "d-notification",
    `d-notification--${size}`,
    `d-notification--${state}`,
    {
      "d-notification--floating": floating,
      "d-notification--plain": plain,
      "d-notification--line-aligned": lineAligned,
    },
  );

  const isLarge = size === "large";
  const role =
    roleOverride ??
    (state === "error" || state === "warning" ? "alert" : "status");
  const dismiss = onDismiss && (
    <DClearButton
      className="d-notification__dismiss"
      color={floating && !plain ? "inherit" : undefined}
      onClick={onDismiss}
    />
  );

  if (plain) {
    return (
      <div className={classes} role={role}>
        <div className="d-flex-1 d-min-w-0">{label}</div>
        {dismiss}
      </div>
    );
  }

  return (
    <div className={classes} role={role}>
      <DIcon
        className="d-notification__icon"
        name={icon ?? STATE_ICONS[state]}
        color="inherit"
      />
      {isLarge ? (
        <div className="d-flex-column d-flex-1 d-gap-10 d-min-w-0">
          <div className="d-flex-column d-gap-4">
            {label && (
              <span className="d-notification__label d-label-s-medium">
                {label}
              </span>
            )}
            {description && (
              <span className="d-notification__description d-paragraph-s-regular">
                {description}
              </span>
            )}
          </div>
          {action && action}
        </div>
      ) : (
        <>
          {label && (
            <span
              className={classNames(
                "d-notification__label d-flex-1 d-min-w-0",
                size === "small" && !floating
                  ? "d-paragraph-x-regular"
                  : "d-label-s-medium",
              )}
            >
              {label}
            </span>
          )}
          {action && action}
        </>
      )}
      {dismiss}
    </div>
  );
};

export default DNotification;
